import React, { useState } from 'react';
import { Loader2, CheckCircle2, Circle, AlertTriangle } from 'lucide-react';
import { atlasApi, AtlasUser, OnboardingEtapaExec } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  execId: string;
  etapas: OnboardingEtapaExec[];
  podeEditar: boolean;
  // Chamado após mudança de etapa, com o novo progresso/status agregado retornados pelo backend.
  onEtapaAlterada?: (info: { etapaId: string; status: 'concluido' | 'pendente'; progresso_pct: number; statusExec: string }) => void;
}

const TIPO_LABEL: Record<string, string> = {
  tarefa: 'Tarefa', documento: 'Documento', treinamento: 'Treinamento', reuniao: 'Reunião',
};

/**
 * Lista de etapas de uma execução de onboarding com ação concluir/reabrir por etapa.
 * Mantém o estado local das etapas para refletir a mudança imediatamente; o pai pode
 * sincronizar progresso via onEtapaAlterada.
 */
const EtapasOnboarding: React.FC<Props> = ({ user, execId, etapas: etapasIniciais, podeEditar, onEtapaAlterada }) => {
  const [etapas, setEtapas] = useState<OnboardingEtapaExec[]>(etapasIniciais);
  const [salvandoId, setSalvandoId] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const toggle = async (et: OnboardingEtapaExec) => {
    if (!podeEditar || salvandoId) return;
    const novoStatus: 'concluido' | 'pendente' = et.status === 'concluido' ? 'pendente' : 'concluido';
    setSalvandoId(et.etapa_id);
    setErro(null);
    try {
      const r = await atlasApi.marcarEtapaOnboarding(user, execId, et.etapa_id, novoStatus);
      setEtapas(prev => prev.map(x => x.etapa_id === et.etapa_id ? { ...x, status: novoStatus } : x));
      onEtapaAlterada?.({ etapaId: et.etapa_id, status: novoStatus, progresso_pct: r.progresso_pct, statusExec: r.status });
    } catch (e: any) {
      setErro(e.message || 'Erro ao atualizar etapa.');
    } finally {
      setSalvandoId(null);
    }
  };

  return (
    <div className="space-y-2">
      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}
      {etapas.length === 0 && <p className="text-sm text-gray-400 italic">Nenhuma etapa.</p>}
      {etapas.map(et => {
        const concluido = et.status === 'concluido';
        const atrasado = et.status === 'atrasado';
        return (
          <div key={et.etapa_id} className={`flex items-start gap-3 p-3 rounded-xl border ${atrasado ? 'border-rose-200 bg-rose-50/50' : 'border-gray-100 bg-gray-50/50'}`}>
            <button
              type="button"
              onClick={() => toggle(et)}
              disabled={!podeEditar || salvandoId === et.etapa_id}
              title={concluido ? 'Reabrir' : 'Concluir'}
              className={`mt-0.5 flex-shrink-0 ${podeEditar ? 'cursor-pointer' : 'cursor-default'} ${concluido ? 'text-emerald-600' : atrasado ? 'text-rose-500' : 'text-gray-300 hover:text-purple-500'}`}
            >
              {salvandoId === et.etapa_id ? <Loader2 className="w-5 h-5 animate-spin" />
                : concluido ? <CheckCircle2 className="w-5 h-5" />
                : <Circle className="w-5 h-5" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium ${concluido ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{et.titulo}</div>
              <div className="text-xs text-gray-400 flex items-center gap-2 flex-wrap mt-0.5">
                <span>{TIPO_LABEL[et.tipo] || et.tipo}</span>
                <span>· dia {et.dias_apos_admissao}</span>
                {atrasado && <span className="text-rose-600 font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Atrasado</span>}
                {concluido && et.concluido_em && <span className="text-emerald-600">· concluído {new Date(et.concluido_em).toLocaleDateString('pt-BR')}</span>}
              </div>
              {et.observacao && <p className="text-xs text-gray-500 mt-1 italic">{et.observacao}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EtapasOnboarding;
